import * as moment from "moment";
import { IPaymentAccount } from "../interfaces";
import * as Globalize from "globalize";
import type { IScope } from "reducers/scope";
import { get } from "lodash";
import { Statement as StatementRecord } from "records/payments/statement";

const ValidStatusHash = {
    Done: "done",
    Pending: "pending",
    Active: "active",
};

export const ValidStatuses = [ValidStatusHash.Active, ValidStatusHash.Pending, ValidStatusHash.Done];

export const getNuorderProcessorAccounts = (accounts: IPaymentAccount[]): IPaymentAccount[] => {
    return accounts.filter((account: IPaymentAccount) => {
        const isActive = ValidStatuses.includes(account.detail.status);
        return account.detail.gateway_name === "NuORDER Payments" && isActive;
    });
};

export const getLsProcessorAccounts = (accounts: IPaymentAccount[]): IPaymentAccount[] => {
    return accounts.filter((account: IPaymentAccount) => {
        const isActive = ValidStatuses.includes(account.detail.status);
        return account.detail.gateway_name === "Lightspeed Payments" && isActive;
    });
};

export const hasActiveNuOrderProcessor = (scope: IScope): boolean => {
    const hasPaymentsEnabled = scope.brand.hasModuleEnabled("payments");
    const accounts = get(scope, "brand.modules.payments.accounts", []);
    const hasProcessorConnected = getNuorderProcessorAccounts(accounts);

    return hasPaymentsEnabled && hasProcessorConnected.length > 0;
};

export const hasActivePaymentAccount = (scope: IScope): boolean => {
    const hasPaymentsEnabled = scope?.brand?.hasModuleEnabled("payments");
    const accounts = get(scope, "brand.modules.payments.accounts", []);
    const hasProcessorConnected = accounts.some((account) => ValidStatuses.includes(account.detail?.status));
    return hasPaymentsEnabled && hasProcessorConnected;
};

export const getYearsFromDate = (date: string, now?: string) => {
    // now arg is helpful for future-proofing the tests
    const currentDate = now ? moment.utc(new Date(now)) : moment.utc();
    const thisYear = currentDate.year();
    const registrationDate = moment.utc(new Date(date));
    const registrationYear = registrationDate.year();
    const diff = thisYear - registrationYear;

    const years = [];

    for (let i = 0; i <= diff; i++) {
        const zeroMonthsTranspired =
            i === 0 &&
            registrationDate.month() === currentDate.month() &&
            registrationDate.year() === currentDate.year();
        if ((registrationDate.month() === 11 && i === 0) || zeroMonthsTranspired) {
            // don't include a year if it was registered in December. That statement will be included
            // in the next year as the January statement
            continue;
        } else {
            years.push(registrationYear + i);
        }
    }

    return years.reverse();
};

const getSelectedAccount = (id: string, accounts: IPaymentAccount[]): IPaymentAccount => {
    return accounts.find((account: IPaymentAccount) => account._id === id);
};

export const getYearsFromAccount = (id: string, accounts: IPaymentAccount[]): number[] => {
    const account = getSelectedAccount(id, accounts);
    const accountRegistrationDate = account?.detail?.registration_date;
    return getYearsFromDate(accountRegistrationDate);
};

export const dateFormatter = (month: number, locale: string, format = "MMMM") => {
    const language = locale.split("-")[0];
    // eslint-disable-next-line
    Globalize.load(require(`../../../../public/locales/${language}`));
    Globalize.locale(language);
    const formatter = Globalize.dateFormatter({ raw: format });

    return formatter(new Date(0, month));
};

export const getDisplayNameForExport = (statement: StatementRecord, locale: string) => {
    return dateFormatter(statement.start_date.month(), locale);
};

export enum CHARGEBACKSTATUS {
    active = "active",
    resolved = "resolved",
}

export const getDefaultAccountId = (accounts: IPaymentAccount[], currencyCode?: string): string => {
    if (!accounts.length) {
        return "";
    }
    let defaultSelectedAccount = accounts[0]._id;
    if (currencyCode) {
        const matching = accounts.find((account: IPaymentAccount) => {
            return account.currency_code.toLowerCase() === currencyCode.toLowerCase();
        });
        if (matching) {
            defaultSelectedAccount = matching._id;
        }
    }

    return defaultSelectedAccount;
};
